/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { Awaited } from '../../../types/typescript';
import getHotelRoomsFromApi from '../hotel/getHotelRooms';
import getHotelById from '../hotel/getHotelById';
import searchHotels from '../hotel/searchHotels';
import type { SelectedMealPlan, GuestCount } from '../../components/booking/meal-plan/types';
import initiatePlanConfigAction from './initiatePlanConfig';
import calculateDiscounts from './calculateDiscounts';
import type DiscountCalculations from '../../../types/DiscountCalculations';
import type { MealPlanConfigModal } from '../../components/booking/meal-plan/PlanConfig/PlanConfig';
import { ValidationError } from '../request';
import { Coupon } from '../../../types/HotelFromApi';
import Campaign from '../../../types/Campaign';
import validateCoupons from './validateCoupons';
import { authService } from '../account/AuthService';
import { DiscountType } from '../constants';

type Hotel = Awaited<ReturnType<typeof searchHotels>>['data'][0];
type HotelRoom = Awaited<ReturnType<typeof getHotelRoomsFromApi>>[0];
type MealPlan = NonNullable<HotelRoom['mealPlans']>[0];
type GetHotelRoomParameters = Parameters<typeof getHotelRoomsFromApi>[0];
type CalculateDiscountsRequestData = Parameters<typeof calculateDiscounts>[0];
type GetHotelByIdParameters = Parameters<typeof getHotelById>[0];
type ValidateCouponsRequestData = Parameters<typeof validateCoupons>[0];

export const NAMESPACE = 'planConfig';

export const getValidCoupons = createAsyncThunk<Coupon[], ValidateCouponsRequestData>(
  `${NAMESPACE}/getValidCoupons`,
  async (
    {
      roomCount,
      totalAmount,
      propertyId,
      language,
      accountId,
      couponCodes,
      discountId,
      zenkokuId,
      discountCodes,
      promotionIds,
      guestPerRooms,
      plans,
    },
    { rejectWithValue, getState }
  ) => {
    const state = getState() as Record<string, any>;
    const affiliateId =
      process.env.NEXT_PUBLIC_FEATURE_FLAG_PARTNERSHIP_PARAMETER === 'true'
        ? state?.booking?.affiliate?.id
        : undefined;

    const validCoupons = await validateCoupons({
      roomCount,
      totalAmount,
      propertyId,
      language,
      accountId,
      couponCodes,
      discountId,
      zenkokuId,
      discountCodes,
      promotionIds,
      guestPerRooms,
      plans,
      affiliate_id: affiliateId,
    }).catch((err: ValidationError) => {
      const errorCode = err.response?.data?.code;

      if (errorCode === 'bad_request') {
        return rejectWithValue('BAD_REQUEST');
      }

      return rejectWithValue('API_FAILURE');
    });

    return validCoupons;
  },
  {
    condition: (_, { getState }) => {
      const { planConfig } = getState() as Record<string, any>;
      if (planConfig.isGetValidCouponsLoading) {
        return false;
      }
      return true;
    },
  }
);

export const getDiscountCalculations = createAsyncThunk<
  {
    discountCalculations: DiscountCalculations;
  },
  {
    calculateDiscountsRequestData: CalculateDiscountsRequestData;
    validCoupons?: Coupon[];
    zenkokuCoupon?: Campaign;
  }
>(
  `${NAMESPACE}/getDiscountCalculations`,
  async ({ calculateDiscountsRequestData, validCoupons, zenkokuCoupon }, { getState }) => {
    const state = getState() as Record<string, any>;
    const affiliateId =
      process.env.NEXT_PUBLIC_FEATURE_FLAG_PARTNERSHIP_PARAMETER === 'true'
        ? state?.booking?.affiliate?.id
        : undefined;

    const discountCalculations = await calculateDiscounts({
      ...calculateDiscountsRequestData,
      affiliate_id: affiliateId,
    });

    return {
      discountCalculations: {
        ...discountCalculations,
        discounts: discountCalculations?.discounts?.map((discount) => {
          if (
            (discount?.discountType === DiscountType.COUPON ||
              discount?.discountType === DiscountType.VOUCHER) &&
            validCoupons?.find((coupon) => coupon.id === discount.id)
          ) {
            return {
              ...discount,
              couponCode: validCoupons?.find((coupon) => coupon.id === discount.id)?.couponCode,
            };
          }

          if (discount.discountType === DiscountType.ZENKOKU && zenkokuCoupon?.id === discount.id) {
            return {
              ...discount,
              couponCode: zenkokuCoupon?.couponCode,
            };
          }

          return discount;
        }),
      },
    };
  },
  {
    condition: (_, { getState }) => {
      const { planConfig } = getState() as Record<string, any>;
      if (planConfig.isGetDiscountsLoading) {
        return false;
      }
      return true;
    },
  }
);

export const getHotel = createAsyncThunk<
  {
    hotel: Hotel;
    room: HotelRoom;
    plans?: SelectedMealPlan[];
    validCoupons?: Coupon[];
  },
  GetHotelByIdParameters &
    GetHotelRoomParameters & {
      selectedHotelRoomId: number;
      selectedMealPlans: SelectedMealPlan[];
      discountIds?: number[];
      zenkokuId?: number;
      couponCodes?: string[];
      promotionIds: string[];
      stayDateFrom: string;
      stayDateTo: string;
    }
>(
  `${NAMESPACE}/getById`,
  async (
    {
      hotelId,
      checkInDate,
      checkOutDate,
      guestCountString,
      roomCount,
      locale,
      selectedHotelRoomId,
      selectedMealPlans,
      discountIds,
      promotionIds,
      zenkokuId,
      couponCodes,
    },
    { rejectWithValue, signal, dispatch }
  ) => {
    try {
      const hotel = await getHotelById({ hotelId, locale }, signal);

      const hotelRooms = await getHotelRoomsFromApi(
        {
          hotelId,
          checkInDate,
          checkOutDate,
          guestCountString,
          roomCount,
          locale,
        },
        signal
      );

      const room = hotelRooms.find(
        (hotelRoom) => hotelRoom.id === selectedHotelRoomId
      ) as HotelRoom;

      let selectedMealPlansWithNewPrices = [] as SelectedMealPlan[];
      if ((selectedMealPlans ?? []).length > 0) {
        selectedMealPlansWithNewPrices = (selectedMealPlans ?? []).reduce(
          (accmulator: SelectedMealPlan[], current) => {
            const selectedMealPlan = room?.mealPlans?.find(
              (plan) => plan?.id === current?.mealPlan?.id
            );
            if (selectedMealPlan !== undefined) {
              accmulator.push({
                ...current,
                mealPlan: selectedMealPlan,
              });
            }
            return accmulator;
          },
          []
        );
      }

      const countArray = guestCountString.split('_');
      let accountId;
      const userSession = await authService.getSession().catch((err) => console.error(err));
      if (userSession?.tokens?.accessToken) {
        accountId = await authService.getAccount(userSession);
      }

      const originalTotalAmount = selectedMealPlans.reduce(
        (acc, mealPlan, i) =>
          acc + (mealPlan.mealPlan?.planRates?.[i]?.dailyTotal?.amountAfterTax || 0),
        0
      );
      const newTotalAmount = selectedMealPlansWithNewPrices.reduce(
        (acc, mealPlan, i) =>
          acc + (mealPlan.mealPlan?.planRates?.[i]?.dailyTotal?.amountAfterTax || 0),
        0
      );

      const newDiscountIds = Array.from(
        new Set(
          selectedMealPlansWithNewPrices.reduce((accumulator: number[], current, i) => {
            const id = current.mealPlan?.planRates?.[i]?.dailyTotal?.discountId;
            if (id) {
              accumulator.push(id);
            }
            return accumulator;
          }, [])
        )
      );

      const newMemberDiscountIds = Array.from(
        new Set(
          selectedMealPlansWithNewPrices.reduce((accumulator: number[], current, i) => {
            const id = current.mealPlan?.planRates?.[i]?.dailyTotal?.memberDiscountId;
            if (id) {
              accumulator.push(id);
            }
            return accumulator;
          }, [])
        )
      );

      const newPromotionIds = Array.from(
        new Set(
          selectedMealPlansWithNewPrices.reduce((accumulator: string[], current, i) => {
            const id = current.mealPlan?.planRates?.[i]?.dailyTotal?.promotionId;
            if (id) {
              accumulator.push(id);
            }
            return accumulator;
          }, [])
        )
      );

      const newMemberPromotionIds = Array.from(
        new Set(
          selectedMealPlansWithNewPrices.reduce((accumulator: string[], current, i) => {
            const id = current.mealPlan?.planRates?.[i]?.dailyTotal?.memberPromotionId;
            if (id) {
              accumulator.push(id);
            }
            return accumulator;
          }, [])
        )
      );

      const discountParams = {
        discountCodes: couponCodes,
        promotionIds: accountId ? newMemberPromotionIds : newPromotionIds,
      };

      let validCoupons: Coupon[] = [];

      if (couponCodes?.length) {
        validCoupons = await validateCoupons({
          ...discountParams,
          totalAmount: newTotalAmount || 0,
          roomCount,
          accountId,
          propertyId: hotelId,
          guestPerRooms: new Array(roomCount).fill({
            adultMaleCount: Number(countArray[0]),
            seniorElementarySchoolChildCount: Number(countArray[1]),
            juniorElementarySchoolChildCount: Number(countArray[2]),
            infantWithMealAndFutonCount: Number(countArray[3]),
            infantWithMealCount: Number(countArray[4]),
            infantWithFutonCount: Number(countArray[5]),
            infantWithoutMealAndFutonCount: Number(countArray[6]),
          }),
          plans: selectedMealPlansWithNewPrices.map((selectedMealPlan) => ({
            date: selectedMealPlan.date,
            planCode: selectedMealPlan.mealPlan?.id,
            planRates: selectedMealPlan.mealPlan?.planRates
              ?.filter((planRate) => planRate.rateEffectiveDate === selectedMealPlan.date)
              .map((targetPlanRate) => ({
                availabilityStatus: targetPlanRate.availabilityStatus,
                rateEffectiveDate: targetPlanRate.rateEffectiveDate,
                rateExpireDate: targetPlanRate.rateExpireDate,
                guestRates: targetPlanRate.guestRates?.map((guestRate) => ({
                  guestType: guestRate.guestType,
                  guestCount: guestRate.guestCount,
                  guestRateTaxIncl: guestRate.guestRateTaxIncl,
                  guestRateType: guestRate.guestRateType,
                  roomPricingType: guestRate.roomPricingType,
                })),
                total: {
                  currencyCode: targetPlanRate.dailyTotal?.currencyCode,
                  amountAfterTax: targetPlanRate.dailyTotal?.amountAfterTax,
                },
              })),
          })),
        }).catch((err: ValidationError) => {
          const errorCode = err.response?.data?.code;

          // if it's BE error, throw an error to navigate 500 page
          if (errorCode !== 'bad_request') {
            throw err;
          }

          // if it's 400, it means coupons are not valid. return empty array.
          return [];
        });
      }

      const zenkokuCoupon = hotel.webCampaigns?.find((webCampaign) => webCampaign.id === zenkokuId);
      const discountParamsForDiscountCalculations = {
        discountCodes: validCoupons?.map((coupon) => coupon.couponCode),
        promotionIds: accountId ? newMemberPromotionIds : newPromotionIds,
      };

      // check if Campaign has been updated or not mostly for the case where locale has been changed to determine if it needs to call calculate discount again
      const isCampaignDiscountUpdated =
        JSON.stringify(discountIds) !==
        JSON.stringify(accountId ? newMemberDiscountIds : newDiscountIds);

      // check if Campaign has been updated or not mostly for the case where login status has been changed to determine if it needs to call calculate discount again
      const isPromotionsUpdated =
        JSON.stringify(promotionIds) !==
        JSON.stringify(accountId ? newMemberPromotionIds : newPromotionIds);

      // DC-2630: call /calculate_discount only when there are any changes in plan's price, any coupons are applied to reduce Voucherify API call, or campaign has been updated mainly due to locale changes
      if (
        originalTotalAmount !== newTotalAmount ||
        validCoupons.length ||
        isCampaignDiscountUpdated ||
        isPromotionsUpdated
      ) {
        await dispatch(
          getDiscountCalculations({
            calculateDiscountsRequestData: {
              ...discountParamsForDiscountCalculations,
              totalAmount: newTotalAmount || 0,
              propertyId: hotelId,
              language: locale,
              accountId,
              zenkokuId: zenkokuCoupon?.id,
              roomCount,
              guestPerRooms: new Array(roomCount).fill({
                adultMaleCount: Number(countArray[0]),
                seniorElementarySchoolChildCount: Number(countArray[1]),
                juniorElementarySchoolChildCount: Number(countArray[2]),
                infantWithMealAndFutonCount: Number(countArray[3]),
                infantWithMealCount: Number(countArray[4]),
                infantWithFutonCount: Number(countArray[5]),
                infantWithoutMealAndFutonCount: Number(countArray[6]),
              }),
              plans: selectedMealPlansWithNewPrices.map((selectedMealPlan) => ({
                date: selectedMealPlan.date,
                planCode: selectedMealPlan.mealPlan?.id,
                planRates: selectedMealPlan.mealPlan?.planRates
                  ?.filter((planRate) => planRate.rateEffectiveDate === selectedMealPlan.date)
                  .map((targetPlanRate) => ({
                    availabilityStatus: targetPlanRate.availabilityStatus,
                    rateEffectiveDate: targetPlanRate.rateEffectiveDate,
                    rateExpireDate: targetPlanRate.rateExpireDate,
                    guestRates: targetPlanRate.guestRates?.map((planRate) => ({
                      guestType: planRate.guestType,
                      guestCount: planRate.guestCount,
                      guestRateTaxIncl: planRate.guestRateTaxIncl,
                      guestRateType: planRate.guestRateType,
                      roomPricingType: planRate.roomPricingType,
                    })),
                    total: {
                      currencyCode: targetPlanRate.dailyTotal?.currencyCode,
                      amountAfterTax: targetPlanRate.dailyTotal?.amountAfterTax,
                    },
                  })),
              })),
            },
            validCoupons,
            zenkokuCoupon,
          })
        );
      }

      return { hotel, room, plans: selectedMealPlansWithNewPrices, validCoupons };
    } catch (err) {
      const error = err as ValidationError;
      console.error(error);

      return rejectWithValue(error.message);
    }
  }
);

const getNewSelectedMealPlans = (
  selectedMealPlans: SelectedMealPlan[],
  targetSelectedMealPlanId: number,
  newlySelectedMealPlanId: string,
  mealPlans: MealPlan[]
) =>
  selectedMealPlans.map<SelectedMealPlan>((p) =>
    p.id === targetSelectedMealPlanId
      ? {
          ...p,
          id: p.id,
          mealPlan: mealPlans.find((plan) => plan.id === newlySelectedMealPlanId),
        }
      : p
  );

export const initialState = {
  selectedHotel: undefined as Hotel | undefined,
  selectedHotelRoom: undefined as HotelRoom | undefined,
  selectedMealPlans: [] as SelectedMealPlan[],
  mealPlanOptions: [] as MealPlan[],
  initiallySelectedMealPlan: undefined as MealPlan | undefined,
  originalTotal: undefined as DiscountCalculations['originalTotal'],
  discountedTotal: undefined as DiscountCalculations['discountedTotal'],
  discounts: undefined as DiscountCalculations['discounts'],
  isFailedToCreateInitialSelectedMealPlans: false,
  isBannerHidden: false,
  isChangeMealPlanButtonHidden: false,
  isLoadingHotel: false,
  isLoadingHotelRejected: false,
  isGetDiscountsLoading: false,
  isGetDiscountsRejected: false,
  isValidateCouponLoading: false,
  isValidateCouponRejected: false,
  isGetValidCouponsLoading: false,
  isGetValidCouponsRejected: false,
  isRoomGetNotAvailable: false,
  mealPlanConfigModal: undefined as MealPlanConfigModal | undefined,
  previousConfigPagePath: '',
};

const planConfigSlice = createSlice({
  name: NAMESPACE,
  initialState,
  reducers: {
    init(
      state,
      action: PayloadAction<{
        selectedHotel: Hotel;
        selectedHotelRoom: HotelRoom;
        initiallySelectedMealPlanId: string;
        tllPlanCode: string;
        stayNights: number;
        checkInDate: string;
        roomCount: number;
        guestCount: GuestCount;
        source: string;
        mealPlanId: string;
      }>
    ) {
      initiatePlanConfigAction(state, action.payload);
    },
    updateSelectedMealPlansForSingleNight(
      state,
      action: PayloadAction<{
        targetSelectedMealPlanId: number;
        newlySelectedMealPlanId: string;
      }>
    ) {
      const { selectedMealPlans } = state;
      const { targetSelectedMealPlanId, newlySelectedMealPlanId } = action.payload;
      const newSelectedMealPlans = getNewSelectedMealPlans(
        selectedMealPlans,
        targetSelectedMealPlanId,
        newlySelectedMealPlanId,
        state.mealPlanOptions
      );

      state.selectedMealPlans = newSelectedMealPlans;
    },
    updateSelectedMealPlansForAllNights(
      state,
      action: PayloadAction<{
        newlySelectedMealPlanId: string;
      }>
    ) {
      const { selectedMealPlans, mealPlanOptions } = state;
      const { newlySelectedMealPlanId } = action.payload;
      const newSelectedMealPlans = selectedMealPlans.map<SelectedMealPlan>((p) => ({
        ...p,
        id: p.id,
        mealPlan: mealPlanOptions.find((plan) => plan.id === newlySelectedMealPlanId),
      }));

      state.selectedMealPlans = newSelectedMealPlans;
    },
    setSelectedMealPlansWithUpdatedPrices(
      state,
      action: PayloadAction<{
        hotel: Hotel;
        updatedSelectedMealPlans: SelectedMealPlan[];
      }>
    ) {
      const { hotel, updatedSelectedMealPlans } = action.payload;

      state.selectedHotel = hotel;
      state.selectedMealPlans = updatedSelectedMealPlans;
    },
    resetDiscount(state) {
      state.originalTotal = undefined;
      state.discountedTotal = undefined;
      state.discounts = undefined;
    },
    setDiscountCalculations(
      state,
      action: PayloadAction<{ discountCalculations: DiscountCalculations }>
    ) {
      const { originalTotal, discountedTotal, discounts } = action.payload.discountCalculations;

      state.originalTotal = originalTotal;
      state.discountedTotal = originalTotal !== discountedTotal ? discountedTotal : undefined;
      state.discounts = discounts;
    },
    openMealPlanConfigModal(state, action: PayloadAction<MealPlanConfigModal | undefined>) {
      state.mealPlanConfigModal = action.payload;
    },
    setPreviousConfigPagePath(state, action) {
      state.previousConfigPagePath = action.payload;
    },
    reset(state) {
      return {
        ...initialState,
        previousConfigPagePath: state.previousConfigPagePath,
      };
    },
  },
  extraReducers: (builder) => {
    // getDiscountCalculations
    builder.addCase(getDiscountCalculations.pending, (state) => {
      state.isGetDiscountsLoading = true;
    });
    builder.addCase(getDiscountCalculations.fulfilled, (state, action) => {
      planConfigSlice.caseReducers.setDiscountCalculations(state, action);
      state.isGetDiscountsLoading = false;
      state.isGetDiscountsRejected = false;
    });
    builder.addCase(getDiscountCalculations.rejected, (state) => {
      state.isGetDiscountsLoading = false;
      state.isGetDiscountsRejected = true;
    });
    // getValidCoupons
    builder.addCase(getValidCoupons.pending, (state) => {
      state.isGetValidCouponsLoading = true;
    });
    builder.addCase(getValidCoupons.fulfilled, (state) => {
      state.isGetValidCouponsLoading = false;
      state.isGetValidCouponsRejected = false;
    });
    builder.addCase(getValidCoupons.rejected, (state) => {
      state.isGetValidCouponsLoading = false;
      state.isGetValidCouponsRejected = true;
    });
    // getHotels
    builder.addCase(getHotel.pending, (state) => {
      state.selectedHotel = undefined;
      state.selectedHotelRoom = undefined;
      state.isLoadingHotel = true;
      state.isRoomGetNotAvailable = false;
      state.isLoadingHotelRejected = false;
    });
    builder.addCase(getHotel.fulfilled, (state, action) => {
      state.selectedHotel = action.payload.hotel;

      if (action.payload.room) {
        state.selectedHotelRoom = action.payload.room;
      } else {
        // case where room availability in TLL gets 0;
        state.isRoomGetNotAvailable = true;
      }

      if (action.payload.plans !== undefined && (action.payload.plans ?? []).length > 0) {
        state.selectedMealPlans = action.payload.plans;
      }
      state.isLoadingHotel = false;
      state.isLoadingHotelRejected = false;
    });
    builder.addCase(getHotel.rejected, (state, action) => {
      state.selectedHotel = undefined;
      state.selectedHotelRoom = undefined;
      state.isLoadingHotel = false;
      if (action.error.name !== 'AbortError') {
        state.isLoadingHotelRejected = true;
      }
    });
  },
});

export default planConfigSlice;
